/**
 * Origin Private File System adapter for large original photo blobs.
 * OPFS keeps big binary data out of IndexedDB (which some browsers cap more
 * aggressively and which is not designed for large blobs). When OPFS is
 * unavailable (older Safari, some private-browsing modes) callers should
 * fall back to the IndexedDB blob store — see `blobStore.ts`.
 */

function isOpfsSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'storage' in navigator &&
    'getDirectory' in navigator.storage
  );
}

let rootPromise: Promise<FileSystemDirectoryHandle> | null = null;

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  if (!isOpfsSupported()) {
    throw new Error('OPFS is niet beschikbaar in deze browser.');
  }
  if (!rootPromise) {
    rootPromise = navigator.storage
      .getDirectory()
      .then((root) => root.getDirectoryHandle('fotoboek-assets', { create: true }));
  }
  return rootPromise;
}

function fileName(projectId: string, assetId: string, kind: string): string {
  return `${projectId}__${assetId}__${kind}`;
}

export const opfsAdapter = {
  isSupported: isOpfsSupported,

  async write(projectId: string, assetId: string, kind: string, blob: Blob): Promise<void> {
    const root = await getRoot();
    const handle = await root.getFileHandle(fileName(projectId, assetId, kind), { create: true });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
  },

  async read(projectId: string, assetId: string, kind: string): Promise<Blob | null> {
    try {
      const root = await getRoot();
      const handle = await root.getFileHandle(fileName(projectId, assetId, kind));
      return await handle.getFile();
    } catch {
      return null;
    }
  },

  async remove(projectId: string, assetId: string, kind: string): Promise<void> {
    try {
      const root = await getRoot();
      await root.removeEntry(fileName(projectId, assetId, kind));
    } catch {
      // Already gone — fine.
    }
  },

  async removeAllForProject(projectId: string): Promise<void> {
    try {
      const root = await getRoot();
      const toRemove: string[] = [];
      for await (const name of (root as unknown as { keys(): AsyncIterable<string> }).keys()) {
        if (name.startsWith(`${projectId}__`)) toRemove.push(name);
      }
      await Promise.all(toRemove.map((name) => root.removeEntry(name).catch(() => undefined)));
    } catch {
      // OPFS unsupported — nothing to clean up here.
    }
  },
};
